import axios from 'axios';
import { getUrl } from '../service-utils';
import authHeader from '../auth-header';
import { IAddTripModel } from '../../components/modals/trip/use-add-trip';
import { ITripModelSummary } from '../../Models/trips/trip-summary.model';
import { ISortBy } from '../../Models/sortby.model';
import { ITrip } from '../../Models/trips/trip.model';

class TripsService {
  private API_URL: string;

  constructor() {
    this.API_URL = getUrl();
  }

  public async deleteTrip(tripId: string): Promise<void> {
    try {
      const response = await axios.delete(`${this.API_URL}trips/${tripId}`, {
        headers: authHeader()
      });
      console.log('Trip deleted successfully:', response.data);
    } catch (error: any) {
      console.error(
        'Error deleting trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async createTrip(trip: IAddTripModel) {
    try {
      const payload = {
        title: trip.title.trim(),
        description: trip.description?.trim() || undefined,
        isPublic: !!trip.isPublic
      };

      const response = await axios.post(this.API_URL + 'trips', payload, {
        headers: authHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error(
        'Error creating trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async getTrips(sort: ISortBy) {
    try {
      const response = await axios.get(
        this.API_URL + `trips?${sort.queryString}`,
        {
          headers: authHeader()
        }
      );
      return response.data.data as ITripModelSummary[];
    } catch (error: any) {
      console.error(
        'Error fetching trips:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async getTrips_Summaries(sort: ISortBy) {
    try {
      const response = await axios.get(
        this.API_URL + `trips/summary?${sort.queryString}`,
        {
          headers: authHeader()
        }
      );
      const trips = response.data.data as ITripModelSummary[];
      return trips;
    } catch (error: any) {
      console.error(
        'Error fetching trips:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async getTripFolderTree() {
    try {
      const response = await axios.get(this.API_URL + 'trips/folder-tree', {
        headers: authHeader()
      });
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error fetching folder tree:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async getTripById(tripId: string) {
    try {
      const response = await axios.get(`${this.API_URL}trips/${tripId}`, {
        headers: authHeader()
      });
      return response.data.data as ITrip;
    } catch (error: any) {
      console.error(
        'Error fetching trip by id:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get all trips for the current user (lightweight list with ID and title)
   * @param limit - Optional limit for number of trips (default: 1000)
   */
  public async getAllTrips(limit: number = 1000) {
    try {
      const response = await axios.get(`${this.API_URL}trips?limit=${limit}`, {
        headers: authHeader()
      });
      return response.data.data as Array<{
        id: string;
        title: string;
        description: string;
        isPublic: boolean;
        destinationCount: number;
        estimatedDuration: number;
        deleted: boolean;
      }>;
    } catch (error: any) {
      console.error(
        'Error fetching all trips:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async updateTrip(tripId: string, trip: Partial<IAddTripModel>) {
    try {
      const response = await axios.put(`${this.API_URL}trips/${tripId}`, trip, {
        headers: authHeader()
      });
      return response.data;
    } catch (error: any) {
      console.error(
        'Error updating trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Copy destinations from one trip to another trip
   * @param sourceTripId - Source trip ID
   * @param targetTripId - Target trip ID
   * @param destinationIds - Optional array of specific destination IDs to copy. If not provided, all destinations will be copied
   */
  public async copyDestinationsToTrip(
    sourceTripId: string,
    targetTripId: string,
    destinationIds?: string[]
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${targetTripId}/destinations/copy`,
        {
          sourceTripId,
          targetTripId,
          destinationIds
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error copying destinations to trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Send a trip copy via email
   * @param tripId - Trip ID to send
   * @param recipientEmail - Email address of recipient
   * @param clean - Whether to remove personal data before sending
   */
  public async sendTripCopy(
    tripId: string,
    recipientEmail: string,
    clean: boolean
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/send-copy`,
        { recipientEmail, clean },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error sending trip copy:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Update trip name and image
   * @param tripId - Trip ID to update
   * @param title - New trip title
   * @param imageUrl - New image URL
   */
  public async updateTripNameAndImage(
    tripId: string,
    title: string,
    imageUrl: string
  ) {
    try {
      const response = await axios.patch(
        `${this.API_URL}trips/${tripId}/name-image`,
        { title, imageUrl },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error updating trip name and image:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Search user's trips with text query
   * @param query - Search text
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   */
  public async searchTrips(
    query: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{ trips: ITripModelSummary[]; pagination?: any }> {
    try {
      const response = await axios.get(
        `${this.API_URL}trips/search/summary?text=${encodeURIComponent(
          query
        )}&page=${page}&limit=${limit}`,
        {
          headers: authHeader()
        }
      );
      return {
        trips: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error: any) {
      console.error(
        'Error searching trips:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Advanced search for user's trips
   * @param searchParams - Search parameters
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   */
  public async advancedSearchTrips(
    searchParams: {
      text?: string;
      tagIds?: string[];
      tagOperator?: 'AND' | 'OR';
      isPublic?: boolean;
      status?: string;
    },
    page: number = 1,
    limit: number = 20
  ): Promise<{ trips: ITripModelSummary[]; pagination?: any }> {
    try {
      const params = new URLSearchParams();
      if (searchParams.text) params.append('text', searchParams.text);
      if (searchParams.tagIds)
        params.append('tagIds', searchParams.tagIds.join(','));
      if (searchParams.tagOperator)
        params.append('tagOperator', searchParams.tagOperator);
      if (searchParams.isPublic !== undefined)
        params.append('isPublic', String(searchParams.isPublic));
      if (searchParams.status) params.append('status', searchParams.status);
      params.append('page', String(page));
      params.append('limit', String(limit));

      const response = await axios.get(
        `${this.API_URL}trips/search/advanced?${params.toString()}`,
        {
          headers: authHeader()
        }
      );
      return {
        trips: response.data.data.trips,
        pagination: response.data.data.pagination
      };
    } catch (error: any) {
      console.error(
        'Error performing advanced search:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  // ============================================================================
  // CUSTOM FOLDER OPERATIONS (Virtual Folders - Phase 5/6)
  // ============================================================================

  /**
   * Get computed folders (system + custom) for a trip
   * Folders are computed at runtime based on activity properties
   */
  public async getComputedFolders(tripId: string) {
    try {
      const response = await axios.get(
        `${this.API_URL}trips/${tripId}/folders`,
        {
          headers: authHeader()
        }
      );
      return response.data.data.folders;
    } catch (error: any) {
      console.error(
        'Error fetching computed folders:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Create a custom folder with filter rules
   */
  public async createCustomFolder(
    tripId: string,
    folderData: {
      title: string;
      description?: string;
      filterRules: {
        types?: string[];
        tags?: string[];
        dateRange?: { start?: Date; end?: Date };
        matchAllTags?: boolean;
      };
    }
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/folders`,
        folderData,
        {
          headers: authHeader()
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error creating custom folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Update a custom folder
   */
  public async updateCustomFolder(
    tripId: string,
    folderId: string,
    updates: {
      title?: string;
      description?: string;
      filterRules?: {
        types?: string[];
        tags?: string[];
        dateRange?: { start?: Date; end?: Date };
        matchAllTags?: boolean;
      };
    }
  ) {
    try {
      const response = await axios.put(
        `${this.API_URL}trips/${tripId}/folders/${folderId}`,
        updates,
        {
          headers: authHeader()
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error updating custom folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Delete a custom folder (soft delete)
   */
  public async deleteCustomFolder(tripId: string, folderId: string) {
    try {
      const response = await axios.delete(
        `${this.API_URL}trips/${tripId}/folders/${folderId}`,
        {
          headers: authHeader()
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error deleting custom folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get activities for a specific folder
   */
  public async getFolderActivities(tripId: string, folderId: string) {
    try {
      const response = await axios.get(
        `${this.API_URL}trips/${tripId}/folders/${folderId}/activities`,
        {
          headers: authHeader()
        }
      );
      return response.data.data.activities;
    } catch (error: any) {
      console.error(
        'Error fetching folder activities:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }
}

const tripsService = new TripsService();
export default tripsService;
